import { useEffect, useRef, useState } from "react";
import { Map as MapIcon } from "lucide-react";

import { useT } from "../../i18n";
import { BASEMAPS } from "../../map/basemaps";
import { cx } from "../../lib/cx";
import { useLayersStore } from "../../store/layersStore";
import { useThemeStore } from "../../store/themeStore";
import { IconButton } from "../ui/IconButton";

/**
 * Base-map picker, moved out of the layers panel to sit with the map controls
 * (zoom / reset / fullscreen). A single icon opens a popover of the base maps
 * valid for the current theme; picking one pins it (see layersStore).
 */
export function BasemapControl() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const resolved = useThemeStore((s) => s.resolved);
  const basemapId = useLayersStore((s) => s.basemapId);
  const setBasemap = useLayersStore((s) => s.setBasemap);

  const basemaps = BASEMAPS.filter((b) => b.themes === "both" || b.themes === resolved);

  // Close on outside click / Esc.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <div data-map-overlay="true" className="rounded-lg border border-border-subtle bg-surface-raised/80 p-1 shadow-md backdrop-blur">
        <IconButton
          label={t.map.basemap}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={open ? "bg-accent-subtle text-accent" : undefined}
        >
          <MapIcon size={18} />
        </IconButton>
      </div>

      {open && (
        <div
          role="radiogroup"
          aria-label={t.map.basemap}
          data-map-overlay="true"
          className="absolute right-full top-0 mr-2 w-48 rounded-panel border border-border-subtle bg-surface-raised/95 p-1.5 shadow-md backdrop-blur"
        >
          {basemaps.map((b) => (
            <label
              key={b.id}
              className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm text-text-primary hover:bg-surface-sunken"
            >
              <input
                type="radio"
                name="basemap-control"
                checked={basemapId === b.id}
                onChange={() => setBasemap(b.id)}
                className="peer sr-only"
              />
              <span
                className={cx(
                  "grid h-4 w-4 shrink-0 place-items-center rounded-full border transition-colors",
                  basemapId === b.id ? "border-accent" : "border-border-strong",
                  "peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent",
                )}
              >
                {basemapId === b.id && <span className="h-2 w-2 rounded-full bg-accent" />}
              </span>
              <span className="flex-1">{t.basemap[b.labelKey]}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
